/**
 * Admin — métriques moteur + paramètres (bloc 2).
 *
 * Lecture seule : distribution des étiquettes calculées, versions de moteur,
 * et aperçu des paramètres (prix énergie, coûts travaux). L'édition des
 * paramètres se fait par fichier YAML + redéploiement (option a, sûre).
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import {
  getAdminConfig,
  getAdminEngineMetrics,
  type LabelCount,
} from "@/api/admin";

const LABEL_COLORS: Record<string, string> = {
  A: "#34c759", B: "#86d94a", C: "#cdd836", D: "#ffd60a",
  E: "#ff9f0a", F: "#ff6a2c", G: "#ff3b30",
};

export default function AdminEnginePage() {
  const metrics = useQuery({
    queryKey: ["admin", "engine-metrics"],
    queryFn: getAdminEngineMetrics,
  });
  const config = useQuery({
    queryKey: ["admin", "config"],
    queryFn: getAdminConfig,
  });

  return (
    <Layout>
      <div className="mx-auto max-w-4xl px-6 py-12">
        <Link to="/admin" className="text-sm text-blue-900 underline">
          ← Admin
        </Link>
        <h1 className="mt-3 text-2xl font-bold">Moteur & paramètres</h1>

        {/* Métriques */}
        <section className="mt-6">
          <h2 className="text-lg font-semibold">Métriques moteur</h2>
          {metrics.isLoading && <p className="mt-2 text-sm text-gray-500">Chargement…</p>}
          {metrics.error && (
            <p className="mt-2 text-sm text-red-700">Impossible de charger les métriques.</p>
          )}
          {metrics.data && (
            <>
              <p className="mt-2 text-sm text-gray-600">
                {metrics.data.dpe_calcule} DPE calculés · {metrics.data.dpe_brouillon} brouillons
              </p>
              <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2">
                <LabelBars title="Étiquettes énergie" data={metrics.data.energy_labels} colored />
                <LabelBars title="Étiquettes GES" data={metrics.data.ges_labels} colored />
              </div>
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700">Versions de moteur</h3>
                <ul className="mt-1 text-sm text-gray-600">
                  {metrics.data.engine_versions.map((v) => (
                    <li key={v.label ?? "?"}>
                      {v.label ?? "—"} : <strong>{v.count}</strong>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </section>

        {/* Paramètres (lecture seule) */}
        <section className="mt-10">
          <h2 className="text-lg font-semibold">Paramètres (lecture seule)</h2>
          <p className="mt-1 text-xs text-gray-500">
            Valeurs actuelles du moteur. L'édition se fait par fichier de
            configuration + redéploiement.
          </p>
          {config.isLoading && <p className="mt-2 text-sm text-gray-500">Chargement…</p>}
          {config.error && (
            <p className="mt-2 text-sm text-red-700">Impossible de charger les paramètres.</p>
          )}
          {config.data && (
            <div className="mt-3 space-y-4">
              <ConfigBlock title="Prix unitaires de l'énergie" value={config.data.energy_unit_prices} />
              <ConfigBlock title="Coûts de travaux" value={config.data.prices} />
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}

function LabelBars({
  title,
  data,
  colored,
}: {
  title: string;
  data: LabelCount[];
  colored?: boolean;
}) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      <div className="mt-2 space-y-1">
        {data.length === 0 && <p className="text-xs text-gray-400">Aucune donnée.</p>}
        {data.map((d) => {
          const lbl = d.label ?? "—";
          return (
            <div key={lbl} className="flex items-center gap-2 text-xs">
              <span className="w-4 font-semibold">{lbl}</span>
              <div className="h-4 flex-1 rounded bg-gray-100">
                <div
                  className="h-4 rounded"
                  style={{
                    width: `${(d.count / max) * 100}%`,
                    backgroundColor: colored ? LABEL_COLORS[lbl] ?? "#8e94a4" : "#1e3a8a",
                    minWidth: d.count > 0 ? "2px" : "0",
                  }}
                />
              </div>
              <span className="w-8 text-right text-gray-600">{d.count}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConfigBlock({ title, value }: { title: string; value: unknown }) {
  return (
    <details className="rounded border border-gray-200 bg-white">
      <summary className="cursor-pointer px-4 py-2 text-sm font-medium">{title}</summary>
      <pre className="overflow-x-auto border-t border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-700">
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  );
}
